import { Injectable } from '@angular/core';
import { ApiCommon } from '@shared/apis/common';
import { Observable } from 'rxjs';
import { ParamsBuilder } from '@shared/models/params-builder';

@Injectable({
  providedIn: 'root',
})
export class DealerApi extends ApiCommon {
  getDealers(pageEvent, q?: any, query?: any): Observable<any> {
    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;
    builder.selectFields = [
      'bpNo',
      'name',
      'address',
      'contacts',
      'tel',
      'email',
      'hq',
      'hqBpNo',
      'status',
      'company',
      'auth',
      'distributorName',
    ];

    if (q) {
      builder.q = q;
    }

    if (query) {
      builder.query = query;
    }

    return this.apiServer.dealer.getDealers(builder.build());
  }

  exportDealers(builder): Observable<any> {
    return this.apiServer.dealer.exportDealers(builder);
  }

  getDealerByHqBpNo(hqBpNo): Observable<any> {
    return this.apiServer.dealer.getDealerByHqBpNo(hqBpNo);
  }

  getUnAssignedDealers(
    pageEvent,
    q?: any,
    query?: any,
    username?: any
  ): Observable<any> {
    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;
    builder.selectFields = [
      'bpNo',
      'name',
      'address',
      'contacts',
      'tel',
      'email',
      'hq',
      'hqBpNo',
      'status',
      'company',
      'auth',
    ];

    if (q) {
      builder.q = q;
    }

    if (query) {
      builder.query = query;
    }
    if (username) {
      builder.username = username;
    }

    return this.apiServer.dealer.getUnAssignedDealers(builder.build());
  }

  getUnAssignedLocationAliases(
    pageEvent,
    q?: any,
    query?: any,
    username?: any
  ): Observable<any> {
    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;
    builder.selectFields = ['bpNo', 'name', 'locationAliases'];

    if (q) {
      builder.q = q;
    }

    if (query) {
      builder.query = query;
    }
    if (username) {
      builder.username = username;
    }

    return this.apiServer.dealer.getUnAssignedLocationAliases(builder.build());
  }

  getNewDealers(pageEvent, q?: any, query?: any): Observable<any> {
    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;
    builder.selectFields = [
      'bpNo',
      'name',
      'address',
      'auth',
      'contacts',
      'tel',
      'email',
      'hq',
      'hqBpNo',
      'status',
      'onboarding',
      'bpType',
      'distributorName',
    ];

    if (q) {
      builder.q = q;
    }

    if (query) {
      builder.query = query;
    }

    return this.apiServer.dealer.getNewDealers(builder.build());
  }

  getDealer(bpNo): Observable<any> {
    return this.apiServer.dealer.getDealer(bpNo);
  }

  checkDuplicateEmail(mainEmail, bpNo?): Observable<any> {
    return this.apiServer.dealer.checkDuplicateEmail(mainEmail, bpNo);
  }

  getHQDealers(): Observable<any> {
    return this.apiServer.dealer.getHQDealers();
  }

  getSelectedHqDealerList(): Observable<any> {
    return this.apiServer.dealer.getSelectedHqDealerList();
  }

  getStores(bpNo): Observable<any> {
    return this.apiServer.dealer.getStores(bpNo);
  }

  getAllStores(bpNo): Observable<any> {
    return this.apiServer.dealer.getAllStores(bpNo);
  }

  getMyStores(): Observable<any> {
    const builder = new ParamsBuilder();
    return this.apiServer.dealer.getMyStores(builder.build());
  }

  getDealerCodes(): Observable<any> {
    return this.apiServer.dealer.getDealerCodes();
  }

  getDealerCodesWithState(): Observable<any> {
    return this.apiServer.dealer.getDealerCodesWithState();
  }

  // Stores for the picked HQs, or the exact stores a saved filter refers to.
  // POSTed because a saved filter can name every store under a large HQ, and a
  // query string of that many ids exceeds the proxy's request-line buffer: the
  // first attempt answered 494 with ~900 ids before reaching the app.
  getFilterStores({ hqIds, storeIds }: { hqIds?: string[]; storeIds?: string[] }): Observable<any> {
    const body: any = {};
    if (storeIds?.length) {
      body.storeIds = storeIds;
    } else if (hqIds?.length) {
      body.hqIds = hqIds;
    }
    return this.apiServer.dealer.getFilterStores(body);
  }

  createHqDealer(dealer): Observable<any> {
    return this.apiServer.dealer.createHqDealer(dealer);
  }

  createDealer(dealer): Observable<any> {
    return this.apiServer.dealer.createDealer(dealer);
  }

  updateDealer(bpNo, dealer): Observable<any> {
    return this.apiServer.dealer.updateDealer(bpNo, dealer);
  }

  deleteRequestedDealer(bpNo): Observable<any> {
    return this.apiServer.dealer.deleteRequestedDealer(bpNo);
  }

  updateRequestStatus(bpNo, onboarding): Observable<any> {
    return this.apiServer.dealer.updateRequestStatus(bpNo, onboarding);
  }

  createContact(bpNo, dealerContact): Observable<any> {
    return this.apiServer.dealer.createDealerContact(bpNo, dealerContact);
  }

  updateContact(bpNo, contactId, dealerContact): Observable<any> {
    return this.apiServer.dealer.updateDealerContact(
      bpNo,
      contactId,
      dealerContact
    );
  }

  deleteContact(bpNo, contactId): Observable<any> {
    return this.apiServer.dealer.deleteDealerContact(bpNo, contactId);
  }

  saveContact(bpNo, dealerContact): Observable<any> {
    return this.apiServer.dealer.saveDealerContact(bpNo, dealerContact);
  }

  getDealerContactForTicket(bpNo, contactId): Observable<any> {
    return this.apiServer.dealer.getDealerContactForTicket(bpNo, contactId);
  }

  createLocationAlias(bpNo, locationAlias): Observable<any> {
    return this.apiServer.dealer.createLocationAlias(bpNo, locationAlias);
  }

  updateLocationAlias(bpNo, locationAliasId, locationAlias): Observable<any> {
    return this.apiServer.dealer.updateLocationAlias(
      bpNo,
      locationAliasId,
      locationAlias
    );
  }

  deleteLocationAlias(bpNo, locationAliasId): Observable<any> {
    return this.apiServer.dealer.deleteLocationAlias(bpNo, locationAliasId);
  }

  saveLocationAlias(bpNo, locationAlias): Observable<any> {
    return this.apiServer.dealer.saveLocationAlias(bpNo, locationAlias);
  }

  checkValidBpNo(bpNo): Observable<any> {
    return this.apiServer.dealer.checkValidBpNo(bpNo);
  }

  saveContactBp(contactData): Observable<any> {
    return this.apiServer.dealer.saveContactBp(contactData);
  }

  updateContactBp(contactData): Observable<any> {
    return this.apiServer.dealer.updateContactBp(contactData);
  }

  getAllDealers(company): Observable<any> {
    return this.apiServer.dealer.getAllDealers(company);
  }

  // B2B
  getBpDealers(): Observable<any> {
    return this.apiServer.dealer.getBpDealers();
  }

  getCpDealers(hqBpNo?): Observable<any> {
    return this.apiServer.dealer.getCpDealers(hqBpNo);
  }

  updateBp(bpNo, bpInfo): Observable<any> {
    return this.apiServer.dealer.updateBp(bpNo, bpInfo);
  }

  createBp(bpInfo): Observable<any> {
    return this.apiServer.dealer.createBp(bpInfo);
  }

  getBPs(pageEvent, q?: any, query?: any): Observable<any> {
    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;
    builder.selectFields = [
      'bpNo',
      'name',
      'address',
      'lastName',
      'firstName',
      'tel',
      'email',
      'hq',
      'status',
      'company',
      'auth',
      'partnerOrganization',
      'rsmVipAgent',
    ];

    if (q) {
      builder.q = q;
    }

    if (query) {
      builder.query = query;
    }

    return this.apiServer.dealer.getDealers(builder.build());
  }

  getCpByBp(hpBpNo): Observable<any> {
    return this.apiServer.dealer.getCpByBp(hpBpNo);
  }

  getCpsByBp(pageEvent, q?: any, query?: any): Observable<any> {
    const builder = new ParamsBuilder();
    builder.pageEvent = pageEvent;
    // builder.selectFields = ['bpNo', 'name', 'address', 'lastName', 'firstName', 'tel', 'email', 'hq', 'status', 'company', 'auth', 'partnerOrganization'];

    if (pageEvent === undefined) {
      builder.sort = '-auth.updatedAt';
    } else if (pageEvent.sort === '') {
      builder.sort = '-auth.updatedAt';
    }

    if (q) {
      builder.q = q;
    }

    if (query) {
      builder.query = query;
    }

    return this.apiServer.dealer.getCpsByBp(builder.build());
  }

  deleteCp(hqBpNo, bpNo): Observable<any> {
    return this.apiServer.dealer.deleteCp(hqBpNo, bpNo);
  }

  getDealersByZipCode(zipcode): Observable<any> {
    return this.apiServer.dealer.getDealersByZipCode(zipcode);
  }

  saveDealerAddressFavorite(storeBpNo, newAddress): Observable<any> {
    return this.apiServer.dealer.saveDealerAddressFavorite(
      storeBpNo,
      newAddress
    );
  }

  deleteDealerAddressFavorite(storeBpNo, newAddress): Observable<any> {
    return this.apiServer.dealer.deleteDealerAddressFavorite(
      storeBpNo,
      newAddress
    );
  }
}
